import React, { useEffect, useState } from 'react';
import { Attendance } from '../../interfaces/attendance.interface';
import { AttendanceService } from '../../services/attendance.service';
import { UserService } from '../../services/user.service';
import { UserType } from '../../interfaces/userType.enum';
import {
    Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    TextField, Typography, CircularProgress, IconButton, Tooltip, TablePagination
} from '@mui/material';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import DownloadIcon from '@mui/icons-material/Download';
import { Edit } from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { displayPersonName } from '../../utils/userUtils';
import { humanizeEnum } from '../../utils/enumLabel';
import { Column } from '../common/responsiveTable.types';
import ResponsiveTable from '../common/ResponsiveTable';

import SearchableSelect from '../common/SearchableSelect';
interface User {
    id: string;
    username: string;
    fullName?: string | null;
    name?: string | null;
    type: UserType;
}

interface UserAttendanceRow {
    user: User;
    attendance: Attendance | undefined;
}

/** One rendered line: a user, their today attendance (if any), and the edit handler for it. */
interface TodayAttendanceRow {
    userId: string;
    username: string;
    /** Shift display name when known, else Title Case of the shift code. */
    shiftLabel?: string;
    isPresent: boolean;
    attendance?: Attendance;
    /** Absent when the user lacks ATTENDANCE_MANAGE (the PUT endpoint's key): no edit icon. */
    onEdit?: (attendance: Attendance) => void;
}

const formatTimeCol = (dateString: string) => {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'HH:mm');
};

/** The backend stamps endTime = startTime at clock-in, so equal times mean "not clocked out yet". */
const formatCheckOut = (a: Attendance) =>
    !a.endTime || a.endTime === a.startTime ? '—' : formatTimeCol(a.endTime);

const TodayStatusChip = ({ isPresent }: { isPresent: boolean }) => (
    <Chip
        data-help="attendance.today.col.status"
        label={isPresent ? 'Present' : 'Absent'}
        size="small"
        color={isPresent ? 'success' : 'error'}
        variant="outlined"
    />
);

// Declared in the OLD table's source order (Username, Check-In Time, Check-Out Time, Status,
// Shift, Instance Status, Late, OT, Actions) — `priority` only picks the card/compact LEAD, it
// never reorders table mode.
export const institutionTodayAttendanceColumns: Column<TodayAttendanceRow>[] = [
    { key: 'username', helpKey: 'attendance.today.col.employee', header: 'Employee', priority: 1, render: r => (
        <Box data-help="attendance.today.col.employee">
            <Typography variant="body2" component="span" sx={{ display: 'block' }}>{r.username}</Typography>
        </Box>
    ) },
    { key: 'checkIn', header: 'Check-In Time', cardLabel: 'In', helpKey: 'attendance.today.col.checkIn', render: r => r.attendance ? formatTimeCol(r.attendance.startTime) : '-' },
    { key: 'checkOut', header: 'Check-Out Time', cardLabel: 'Out', helpKey: 'attendance.today.col.checkOut', render: r => r.attendance ? formatCheckOut(r.attendance) : '-' },
    { key: 'status', header: 'Status', priority: 1, helpKey: 'attendance.today.col.status', render: r => <TodayStatusChip isPresent={r.isPresent} /> },
    { key: 'shift', header: 'Shift', helpKey: 'attendance.today.col.shift', render: r => r.attendance?.shiftCode ? <Chip label={r.shiftLabel || humanizeEnum(r.attendance.shiftCode)} size="small" /> : '-' },
    { key: 'instanceStatus', header: 'Instance Status', helpKey: 'attendance.today.col.instanceStatus', render: r => humanizeEnum(r.attendance?.instanceStatus) || '-' },
    { key: 'late', header: 'Late', helpKey: 'attendance.today.col.late', render: r => r.attendance?.isLate ? <Chip label={`${r.attendance.lateMinutes}m`} size="small" color="error" /> : '-' },
    { key: 'overtime', header: 'OT', helpKey: 'attendance.today.col.overtime', render: r => (r.attendance?.overtimeMinutes && r.attendance.overtimeMinutes > 0) ? <Chip label={`${r.attendance.overtimeMinutes}m`} size="small" /> : '-' },
    {
        key: 'actions', header: 'Actions', helpKey: 'attendance.today.col.actions', role: 'actions', render: r => r.attendance && r.onEdit && (
            <Tooltip title="Edit Attendance" arrow>
                <IconButton
                    data-help="attendance.today.action.edit"
                    size="small"
                    onClick={() => r.onEdit!(r.attendance!)}
                    color="primary"
                    aria-label="Edit Attendance"
                >
                    <Edit />
                </IconButton>
            </Tooltip>
        ),
    },
];

const InstitutionTodayAttendance: React.FC = () => {
    const canExportAttendance = usePermission(PERMISSIONS.ATTENDANCE_EXPORT);
    const canManageAttendance = usePermission(PERMISSIONS.ATTENDANCE_MANAGE);
    const [rows, setRows] = useState<UserAttendanceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportStartDate, setExportStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [exportEndDate, setExportEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [exporting, setExporting] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingAttendance, setEditingAttendance] = useState<Attendance | null>(null);
    const [editStartTime, setEditStartTime] = useState('');
    const [editEndTime, setEditEndTime] = useState('');
    // A night shift clocks out after midnight: the end time then falls on the day after the record date.
    const [editEndsNextDay, setEditEndsNextDay] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const toast = useToast();
    const { shifts } = useShiftLookup();

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersResponse, todayAttendances, roster] = await Promise.all([
                UserService.getInstitutionUsers(0, 200, undefined, undefined, true),
                AttendanceService.getInstitutionTodayAttendance(),
                // A failed roster read keeps the previous (unfiltered) behaviour rather than
                // blanking the screen.
                Promise.resolve()
                    .then(() => AttendanceService.getInstitutionTodayRoster())
                    .catch(() => null),
            ]);
            const allUsers: User[] = usersResponse.content ?? usersResponse;
            // A department head's records are already team-only; list only the team too, so
            // people outside it aren't shown as "Absent".
            const inScope = roster?.restricted ? new Set(roster.userIds) : null;
            const users = inScope ? allUsers.filter(u => inScope.has(u.id)) : allUsers;
            const merged: UserAttendanceRow[] = users.map(user => ({
                user,
                attendance: todayAttendances.find(a => a.userId === user.id),
            }));
            setRows(merged);
        } catch (err) {
            toast.showApiError(err, "Failed to fetch today's attendance");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenEditDialog = (attendance: Attendance) => {
        setEditingAttendance(attendance);
        setEditStartTime(format(parseISO(attendance.startTime), 'HH:mm'));
        setEditEndTime(format(parseISO(attendance.endTime), 'HH:mm'));
        setEditEndsNextDay(format(parseISO(attendance.endTime), 'yyyy-MM-dd') > attendance.date);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingAttendance(null);
    };

    const handleSaveEdit = async () => {
        if (!editingAttendance) return;
        try {
            setSaving(true);
            const date = editingAttendance.date;
            const startTime = new Date(`${date}T${editStartTime}:00`).toISOString();
            const end = new Date(`${date}T${editEndTime}:00`);
            if (editEndsNextDay) end.setDate(end.getDate() + 1);
            const endTime = end.toISOString();
            await AttendanceService.updateAttendance(editingAttendance.id, startTime, endTime);
            toast.showSuccess('Attendance updated successfully');
            handleCloseEditDialog();
            await fetchData();
        } catch (err) {
            toast.showApiError(err, 'Failed to update attendance');
        } finally {
            setSaving(false);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            await AttendanceService.downloadMonthlyReport(
                new Date(exportStartDate),
                new Date(exportEndDate)
            );
            setExportDialogOpen(false);
        } catch (err) {
            toast.showApiError(err, 'Failed to download attendance report');
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <Typography>Loading...</Typography>;

    const filteredRows = rows.filter(({ attendance }) =>
        !selectedShiftId || attendance?.shiftId === selectedShiftId);
    const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const responsiveRows: TodayAttendanceRow[] = paginatedRows.map(({ user, attendance }) => ({
        userId: user.id,
        username: displayPersonName(user.fullName || user.name, user.username),
        shiftLabel: attendance?.shiftName
            || (attendance?.shiftId ? shifts.find(s => s.id === attendance.shiftId)?.name : undefined),
        isPresent: !!attendance,
        attendance,
        onEdit: canManageAttendance ? handleOpenEditDialog : undefined,
    }));

    return (
        <Box data-help-page="attendance.today.page" sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5">
                        Today's Attendance
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {format(new Date(), 'dd-MM-yyyy')}
                    </Typography>
                </Box>
                <Box data-help-section="attendance.today.filters" sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <SearchableSelect
                        data-help="attendance.today.shiftFilter"
                        options={shifts.filter(s => s.isActive)}
                        value={selectedShiftId}
                        onChange={(id) => { setSelectedShiftId(id); setPage(0); }}
                        getOptionLabel={s => `${s.code} – ${s.name}`}
                        label="Shift"
                        placeholder="All shifts"
                        sx={{ flex: '1 1 160px', maxWidth: 260 }}
                    />
                    {canExportAttendance && (
                        <Button
                            data-help="attendance.today.export"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => setExportDialogOpen(true)}
                        >
                            Export Monthly Report
                        </Button>
                    )}
                </Box>
            </Box>

            <ResponsiveTable<TodayAttendanceRow> data-help-section="attendance.today.table"
                pageKey="attendance.today"
                columns={institutionTodayAttendanceColumns}
                rows={responsiveRows}
                getRowKey={r => r.userId}
                emptyMessage="No users found"
            />
            <TablePagination
                data-help="attendance.today.paging"
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
            />

            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} PaperProps={{ 'data-help-page': 'attendance.editForm.page' } as any} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Attendance</DialogTitle>
                <DialogContent data-help-section="attendance.editForm.section.details">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            data-help="attendance.editForm.field.date"
                            label="Date"
                            value={format(new Date(), 'dd-MM-yyyy')}
                            disabled
                            fullWidth
                        />
                        <TextField
                            data-help="attendance.today.editStart"
                            label="Start Time"
                            type="time"
                            value={editStartTime}
                            onChange={(e) => setEditStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            data-help="attendance.today.editEnd"
                            label="End Time"
                            type="time"
                            value={editEndTime}
                            onChange={(e) => setEditEndTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <FormControlLabel
                            data-help="attendance.editForm.field.endsNextDay"
                            control={<Checkbox checked={editEndsNextDay} onChange={(e) => setEditEndsNextDay(e.target.checked)} />}
                            label="Ends next day (clock-out after midnight)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button data-help="attendance.editForm.cancel" onClick={handleCloseEditDialog} disabled={saving}>Cancel</Button>
                    <Button data-help="attendance.editForm.save" onClick={handleSaveEdit} variant="contained" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} PaperProps={{ 'data-help-page': 'attendance.exportForm.page' } as any}>
                <DialogTitle>Export Monthly Attendance Report</DialogTitle>
                <DialogContent data-help-section="attendance.exportForm.section.details">
                    <Box sx={{ display: 'flex', gap: 2, marginTop: 1 }}>
                        <TextField
                            data-help="attendance.today.exportStart"
                            label="Start Date"
                            type="date"
                            value={exportStartDate}
                            onChange={(e) => setExportStartDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            data-help="attendance.today.exportEnd"
                            label="End Date"
                            type="date"
                            value={exportEndDate}
                            onChange={(e) => setExportEndDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button data-help="attendance.exportForm.cancel" onClick={() => setExportDialogOpen(false)} disabled={exporting}>
                        Cancel
                    </Button>
                    <Button
                        data-help="attendance.exportForm.save"
                        onClick={handleExport}
                        variant="contained"
                        disabled={exporting}
                        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    >
                        {exporting ? 'Downloading...' : 'Download'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InstitutionTodayAttendance;
